import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";

type RatingInputProps = {
  max: number;
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
  label?: string;
};

export function RatingInput({
  max,
  value,
  onChange,
  disabled,
  label = "Rating",
}: RatingInputProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useSignal(false);

  const fillPct = value ? ((value - 1) / Math.max(max - 1, 1)) * 100 : 0;

  function setFromPosition(clientX: number) {
    if (disabled || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const val = Math.round(ratio * (max - 1)) + 1;
    onChange(val);
  }

  // Mouse drag
  useEffect(() => {
    if (disabled) return;

    const onMove = (e: MouseEvent) => {
      if (dragging.value) setFromPosition(e.clientX);
    };
    const onUp = () => {
      dragging.value = false;
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [disabled]);

  // Touch drag
  useEffect(() => {
    if (disabled) return;

    const onMove = (e: TouchEvent) => {
      if (dragging.value && e.touches[0]) {
        setFromPosition(e.touches[0].clientX);
      }
    };
    const onEnd = () => {
      dragging.value = false;
    };

    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    return () => {
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [disabled]);

  return (
    <fieldset class="border-0 p-2 m-0 w-full max-w-sm">
      <legend class="sr-only">{label}</legend>

      {/* Slider */}
      <div class={`relative ${disabled ? "py-1" : "pt-7 pb-1"}`}>
        {/* Current value label (edit mode only) */}
        {!disabled && value && (
          <div
            class="absolute top-0 text-xs font-bold text-text-secondary transition-all duration-300 ease-out"
            style={{ left: `${fillPct}%`, transform: "translateX(-50%)" }}
          >
            {value}
          </div>
        )}

        {/* Track */}
        <div
          ref={trackRef}
          class={`relative h-[6px] rounded-[3px] bg-border-muted ${disabled ? "" : "cursor-pointer"}`}
          onClick={(e) => {
            if (!disabled) setFromPosition(e.clientX);
          }}
          onKeyDown={() => {}}
          role="presentation"
        >
          {/* Fill */}
          <div
            class="absolute left-0 top-0 bottom-0 rounded-[3px] transition-[width] duration-300 ease-out"
            style={{
              width: value ? `${fillPct}%` : "0%",
              background:
                "linear-gradient(90deg, #b8c4bc, #8aaab8, #4a80c0, #0050cc)",
            }}
          />

          {/* Stop dots */}
          <div class="absolute inset-0 flex justify-between items-center">
            {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`${n} of ${max}`}
                aria-pressed={value === n}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) onChange(n);
                }}
                class={`relative z-[2] rounded-full transition-all duration-300 ${
                  disabled ? "" : "cursor-pointer"
                } ${
                  value && n <= value
                    ? "size-[9px] bg-white/90"
                    : "size-[9px] bg-[#ced5dc]"
                }`}
              />
            ))}
          </div>

          {/* Thumb (edit mode only) */}
          {!disabled && value && (
            <div
              class="absolute top-1/2 z-[3] size-[26px] rounded-full bg-[#0050cc] -translate-y-1/2 -translate-x-1/2 transition-[left] duration-300 ease-out cursor-grab shadow-[0_2px_10px_rgba(0,80,204,0.3)] hover:shadow-[0_0_0_8px_rgba(0,80,204,0.08)]"
              style={{ left: `${fillPct}%` }}
              onMouseDown={(e) => {
                e.preventDefault();
                dragging.value = true;
              }}
              onTouchStart={() => {
                dragging.value = true;
              }}
              role="presentation"
            />
          )}
        </div>
      </div>

      {/* Number row + endpoint labels */}
      <div class="flex justify-between items-start mt-2.5">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => !disabled && onChange(n)}
            class={`text-[11px] font-medium transition-all w-[9px] text-center ${
              disabled ? "cursor-default" : "cursor-pointer"
            } ${
              value === n ? "text-text-secondary font-bold" : "text-text-subtle"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div class="flex justify-between mt-1">
        <span class="text-[11px] text-text-subtle font-medium">Not great</span>
        <span class="text-[11px] text-text-subtle font-medium">Amazing!</span>
      </div>
    </fieldset>
  );
}
